import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarCheck, CheckCheck, Loader2, SkipForward, Spline } from "lucide-react";
import { toast } from "sonner";
import { crmApi } from "@/lib/ownerClient";

const STATUS_VARIANTS = {
  scheduled: "border-amber-500/40 text-amber-300",
  due: "border-orange-500/40 text-orange-300",
  completed: "border-emerald-500/40 text-emerald-300",
  skipped: "border-zinc-600 text-zinc-400",
  cancelled: "border-rose-500/40 text-rose-300",
};

function CompleteDialog({ dispatch, onSaved, onClose }) {
  const [tech, setTech] = useState("");
  const [company, setCompany] = useState("Duo Pest Defense");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await crmApi.completeDispatch(dispatch.id, { technician_name: tech, technician_company: company, notes });
      toast.success("Dispatch marked complete");
      onSaved?.();
      onClose();
    } catch (exc) {
      toast.error(exc.message || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-black border-zinc-800 text-white">
        <DialogHeader><DialogTitle>Complete dispatch · visit #{dispatch.sequence}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-zinc-400">Scheduled for {dispatch.scheduled_date} · {dispatch.completion_window_label}</div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-zinc-400">Technician name</Label>
            <Input value={tech} onChange={(e) => setTech(e.target.value)} className="bg-zinc-950 border-zinc-700 text-white" data-testid="complete-technician" placeholder="Marco Reyes" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-zinc-400">Company</Label>
            <Input value={company} onChange={(e) => setCompany(e.target.value)} className="bg-zinc-950 border-zinc-700 text-white" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-zinc-400">Notes</Label>
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className="bg-zinc-950 border-zinc-700 text-white" placeholder="Treated perimeter, customer requested no interior service." />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose} disabled={busy} className="text-zinc-300">Cancel</Button>
            <Button onClick={submit} disabled={busy} className="bg-amber-500 text-black hover:bg-amber-400" data-testid="confirm-complete">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCheck className="h-4 w-4 mr-1" /> Mark complete</>}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SkipDialog({ dispatch, onSaved, onClose }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!reason.trim()) { toast.error("Reason required"); return; }
    setBusy(true);
    try {
      await crmApi.skipDispatch(dispatch.id, reason);
      toast.success("Dispatch skipped");
      onSaved?.();
      onClose();
    } catch (exc) {
      toast.error(exc.message || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-black border-zinc-800 text-white">
        <DialogHeader><DialogTitle>Skip dispatch · visit #{dispatch.sequence}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-zinc-400">Reason</Label>
            <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} className="bg-zinc-950 border-zinc-700 text-white" data-testid="skip-reason" placeholder="Customer requested reschedule" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose} disabled={busy} className="text-zinc-300">Cancel</Button>
            <Button onClick={submit} disabled={busy} className="bg-rose-500/80 hover:bg-rose-500 text-white" data-testid="confirm-skip">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><SkipForward className="h-4 w-4 mr-1" /> Confirm skip</>}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function DispatchesView() {
  const [list, setList] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [upcomingOnly, setUpcomingOnly] = useState(true);
  const [acting, setActing] = useState(null); // {type:'complete'|'skip', dispatch}

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = { upcoming_only: upcomingOnly };
      if (statusFilter) params.status = statusFilter;
      const [d, m] = await Promise.all([crmApi.listDispatches(params), crmApi.listMembers({ limit: 500 })]);
      setList(d || []);
      setMembers(m || []);
    } catch (exc) {
      toast.error(exc.message || "Failed to load dispatches");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, upcomingOnly]);

  useEffect(() => {
    let active = true;
    const params = { upcoming_only: upcomingOnly };
    if (statusFilter) params.status = statusFilter;
    (async () => {
      try {
        const [d, m] = await Promise.all([crmApi.listDispatches(params), crmApi.listMembers({ limit: 500 })]);
        if (!active) return;
        setList(d || []);
        setMembers(m || []);
      } catch (exc) {
        if (active) toast.error(exc.message || "Failed to load dispatches");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [statusFilter, upcomingOnly]);

  const memberLookup = (id) => members.find((m) => m.id === id) || { customer_name: "—", customer_email: "" };

  return (
    <div className="space-y-6" data-testid="owner-dispatches">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2"><Spline className="h-5 w-5 text-amber-300" /> Pest Dispatches</h1>
          <p className="text-sm text-zinc-400 mt-1">Bi-monthly Duo Pest Defense visits for Plus & Premier members.</p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-44 bg-zinc-950 border-zinc-700 text-white" data-testid="dispatches-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {Object.keys(STATUS_VARIANTS).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button size="sm" variant="outline" onClick={() => setUpcomingOnly((v) => !v)} className="border-zinc-700 text-white hover:bg-zinc-900" data-testid="dispatches-upcoming-toggle">
          {upcomingOnly ? "Show all dates" : "Upcoming only"}
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-400 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : list.length === 0 ? (
        <Card className="bg-black/40 border-zinc-800">
          <CardContent className="py-10 text-center text-sm text-zinc-400">
            No dispatches match the current filter. Add a Plus/Premier member to start generating bi-monthly visits.
          </CardContent>
        </Card>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-zinc-800 bg-black/40">
          <table className="w-full text-sm" data-testid="dispatches-table">
            <thead className="text-xs uppercase text-zinc-500 border-b border-zinc-800">
              <tr>
                <th className="text-left py-2 px-3">When</th>
                <th className="text-left py-2 px-3">Member</th>
                <th className="text-left py-2 px-3">Status</th>
                <th className="text-left py-2 px-3">Tech</th>
                <th className="text-right py-2 px-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.map((d) => {
                const m = memberLookup(d.member_id);
                return (
                  <tr key={d.id} className="border-b border-zinc-900 hover:bg-zinc-900/30" data-testid={`dispatch-row-${d.id}`}>
                    <td className="py-2 px-3">
                      <div className="text-white">{d.scheduled_date}</div>
                      <div className="text-[11px] text-zinc-500">visit #{d.sequence} · {d.completion_window_label}</div>
                    </td>
                    <td className="py-2 px-3">
                      <div className="text-white">{m.customer_name}</div>
                      <div className="text-[11px] text-zinc-500">{m.customer_email}</div>
                    </td>
                    <td className="py-2 px-3">
                      <Badge variant="outline" className={STATUS_VARIANTS[d.status] || "border-zinc-700 text-zinc-300"}>
                        {d.status}
                      </Badge>
                    </td>
                    <td className="py-2 px-3 text-zinc-300">
                      {d.technician_name || "—"}
                      <div className="text-[11px] text-zinc-500">{d.technician_company || ""}</div>
                    </td>
                    <td className="py-2 px-3">
                      <div className="flex justify-end gap-1">
                        {["scheduled", "due"].includes(d.status) && (
                          <>
                            <Button size="sm" variant="ghost" className="text-emerald-300 hover:bg-emerald-500/10" onClick={() => setActing({ type: "complete", dispatch: d })} data-testid={`complete-dispatch-${d.id}`}>
                              <CalendarCheck className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="ghost" className="text-rose-300 hover:bg-rose-500/10" onClick={() => setActing({ type: "skip", dispatch: d })} data-testid={`skip-dispatch-${d.id}`}>
                              <SkipForward className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {acting?.type === "complete" && (
        <CompleteDialog dispatch={acting.dispatch} onSaved={reload} onClose={() => setActing(null)} />
      )}
      {acting?.type === "skip" && (
        <SkipDialog dispatch={acting.dispatch} onSaved={reload} onClose={() => setActing(null)} />
      )}
    </div>
  );
}
